export interface IAcademicsCourses {
  id: number;
  name: string;
  description: string;
  subjectCode: string;
  classIds: number[]; // References to multiple classes where this course is taught
  teacherId: number;
  subjectType: ESubjectType;
  syllabus?: string;
  totalLectures?: number;
  isElective: boolean;
  weightage?: number; // Marks weightage in final assessment
  practicalsIncluded?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IAcademicsClasses {
  id: number;
  name: string;
  description: string;
  section?: EAcademicSection;
  sections?: { id: string; name: string }[];
  standardLevel: EStandardLevel;
  board?: EAcademicBoard;
  academicYear: string;
  classTutor?: number; // Teacher ID for class teacher
  strength?: number; // Number of students
  stream?: EAcademicStream; // For higher classes
  courseIds?: number[]; // References to courses taught in this class
  createdAt: string;
  updatedAt: string;
}

// ============================================================
// API Request/Response Interfaces (matching backend API)
// ============================================================

// --- Classes API Types ---

export interface IClassRequest {
  class: {
    instituteId: string;
    name: string;
    description?: string;
    section?: string;
    standardLevelId: string;
    academicYear: string;
    classTutorId?: string;
    strength?: number;
    createdBy?: string;
  };
}

export interface IClassResponse {
  id: string;
  name: string;
  description?: string;
  section?: string;
  standardLevel: {
    id: string;
    name?: string;
  };
  academicYear: string;
  classTutor?: {
    id: string;
    name?: string;
  };
  strength?: number;
  institute: {
    id: string;
    name?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface IClassListQueryParams {
  page?: number;
  limit?: number;
  instituteId?: string;
  academicYear?: string;
  standardLevel?: string;
  section?: string;
  search?: string;
}

// --- Courses API Types ---

export interface ICourseRequest {
  course: {
    instituteId: string;
    name: string;
    subjectCode: string;
    description?: string;
    teacherId?: string;
    subjectType: 'core' | 'elective' | 'language' | 'vocational';
    syllabus?: string;
    totalLectures?: number;
    isElective?: boolean;
    weightage?: number;
    practicalsIncluded?: boolean;
    academicYear?: string;
  };
}

export interface ICourseResponse {
  id: string;
  name: string;
  description?: string;
  subjectCode: string;
  teacher?: {
    id: string;
    name?: string;
  };
  subjectType: string;
  syllabus?: string;
  totalLectures?: number;
  isElective: boolean;
  weightage?: number;
  practicalsIncluded?: boolean;
  academicYear?: string;
  institute: {
    id: string;
    name?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ICourseListQueryParams {
  page?: number;
  limit?: number;
  instituteId?: string;
  academicYear?: string;
  classId?: string;
  teacherId?: string;
  subjectType?: string;
  isElective?: boolean;
  search?: string;
}

// Enums for standardized options
export enum EAcademicBoard {
  CBSE = 'CBSE',
  ICSE = 'ICSE',
  STATE = 'State Board',
  IB = 'International Baccalaureate',
  IGCSE = 'IGCSE',
  NIOS = 'NIOS',
}

export enum EAcademicStream {
  SCIENCE = 'Science',
  COMMERCE = 'Commerce',
  ARTS = 'Arts',
  VOCATIONAL = 'Vocational',
  GENERAL = 'General',
}

export enum EStandardLevel {
  // Primary
  CLASS_1 = 'Class 1',
  CLASS_2 = 'Class 2',
  CLASS_3 = 'Class 3',
  CLASS_4 = 'Class 4',
  CLASS_5 = 'Class 5',
  // Middle
  CLASS_6 = 'Class 6',
  CLASS_7 = 'Class 7',
  CLASS_8 = 'Class 8',
  // Secondary
  CLASS_9 = 'Class 9',
  CLASS_10 = 'Class 10',
  // Senior Secondary
  CLASS_11 = 'Class 11',
  CLASS_12 = 'Class 12',
}

export enum EAcademicSection {
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  E = 'E',
  F = 'F',
}

export enum ESubjectType {
  LANGUAGE = 'Language',
  SCIENCE = 'Science',
  MATHEMATICS = 'Mathematics',
  SOCIAL_STUDIES = 'Social Studies',
  COMPUTER = 'Computer',
  PHYSICAL_EDUCATION = 'Physical Education',
  ARTS = 'Arts',
  VOCATIONAL = 'Vocational',
  OTHER = 'Other',
}

